(() => {
  'use strict';

  const GLOBAL_FLAG = '__entrevoltasUniqueStampCatalogLoaded';
  const GRID_SELECTOR = '[data-entrevoltas-diversity-grid="collection"]';
  const PRODUCT_SELECTOR = '[data-entrevoltas-product-card]';
  const CONTAINER_SELECTOR = '#ProductGridContainer';
  const SOURCE_BATCH_SIZE = 3;
  const MAX_CACHE_ENTRIES = 36;
  const cache = new Map();
  const states = new WeakMap();

  const normalize = (value) =>
    String(value || '')
      .trim()
      .replace(/\s+/g, ' ')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('pt-BR');

  const fallbackPrintKey = (card) => {
    const title = String(card.dataset.entrevoltasProductTitle || '').trim();
    const parts = title.split(/\s+(?:—|–|-)\s+/);
    const stamp = parts.length > 1 ? parts.slice(1).join(' - ') : title;
    return `title:${normalize(stamp)}`;
  };

  const printKey = (card) => {
    const registryKey = String(card.dataset.entrevoltasPrintKey || '').trim();
    return registryKey || fallbackPrintKey(card);
  };

  const currentUniquePage = () => {
    const raw = new URLSearchParams(window.location.search).get('page');
    const parsed = Number.parseInt(raw || '1', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  };

  const baseParams = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('section_id');
    return params;
  };

  const querySignature = (sectionId) => {
    const params = baseParams();
    params.sort();
    return `${window.location.pathname}?${params.toString()}#${sectionId}`;
  };

  const sourceUrl = (sectionId, sourcePage) => {
    const url = new URL(window.location.pathname, window.location.origin);
    const params = baseParams();
    params.set('page', String(sourcePage));
    params.set('section_id', sectionId);
    url.search = params.toString();
    return url.toString();
  };

  const fetchSourceGrid = async (sectionId, sourcePage, signal) => {
    const key = `${querySignature(sectionId)}|source:${sourcePage}`;
    if (cache.has(key)) return cache.get(key).cloneNode(true);

    const response = await fetch(sourceUrl(sectionId, sourcePage), {
      signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) {
      throw new Error(`Collection source page ${sourcePage} returned ${response.status}`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const grid = doc.querySelector(GRID_SELECTOR);

    if (!grid) {
      throw new Error(`Collection source page ${sourcePage} has no product grid`);
    }

    if (cache.size >= MAX_CACHE_ENTRIES) {
      const oldestKey = cache.keys().next().value;
      if (oldestKey) cache.delete(oldestKey);
    }

    cache.set(key, grid.cloneNode(true));
    return grid;
  };

  const collectUniqueCards = async ({
    grid,
    sectionId,
    requestedPage,
    pageSize,
    signal,
  }) => {
    const wantedEnd = requestedPage * pageSize;
    const wantedWithLookahead = wantedEnd + 1;
    const groups = new Map();

    let sourcePage = 1;
    let sourcePages = Number.parseInt(grid.dataset.entrevoltasSourcePages || '1', 10) || 1;

    const absorbGrid = (sourceGrid) => {
      sourcePages =
        Number.parseInt(sourceGrid.dataset.entrevoltasSourcePages || String(sourcePages), 10) ||
        sourcePages;

      const cards = Array.from(sourceGrid.children).filter((child) =>
        child.matches(PRODUCT_SELECTOR)
      );

      cards.forEach((card) => {
        const key = printKey(card);
        if (!groups.has(key)) groups.set(key, card.cloneNode(true));
      });
    };

    while (sourcePage <= sourcePages && groups.size < wantedWithLookahead) {
      if (sourcePage === 1 && requestedPage === 1) {
        absorbGrid(grid.cloneNode(true));
        sourcePage += 1;
        continue;
      }

      const lastPageInBatch = Math.min(sourcePages, sourcePage + SOURCE_BATCH_SIZE - 1);
      const pages = [];

      for (let page = sourcePage; page <= lastPageInBatch; page += 1) {
        pages.push(page);
      }

      const sourceGrids = await Promise.all(
        pages.map((page) => fetchSourceGrid(sectionId, page, signal))
      );

      sourceGrids.forEach(absorbGrid);
      sourcePage = lastPageInBatch + 1;
    }

    return {
      cards: Array.from(groups.values()),
      sourceExhausted: sourcePage > sourcePages,
    };
  };

  const pageHref = (page) => {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');

    if (page <= 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', String(page));
    }

    return `${url.pathname}${url.search}${url.hash}`;
  };

  const renderPager = ({ grid, page, hasNext }) => {
    const collection = grid.closest('.collection');
    if (!collection) return;

    const nativePager = collection.querySelector('.pagination-wrapper:not(.ev-stamp-pagination)');
    if (nativePager) nativePager.hidden = true;

    let pager = collection.querySelector('.ev-stamp-pagination');

    if (!pager) {
      pager = document.createElement('div');
      pager.className = 'pagination-wrapper ev-stamp-pagination';
      grid.insertAdjacentElement('afterend', pager);
    }

    if (page <= 1 && !hasNext) {
      pager.replaceChildren();
      pager.hidden = true;
      return;
    }

    pager.hidden = false;
    pager.innerHTML = '';

    const nav = document.createElement('nav');
    nav.className = 'pagination';
    nav.setAttribute('role', 'navigation');
    nav.setAttribute('aria-label', 'Paginação das estampas');

    const list = document.createElement('ul');
    list.className = 'pagination__list list-unstyled';
    list.setAttribute('role', 'list');

    if (page > 1) {
      const prevItem = document.createElement('li');
      const prev = document.createElement('a');
      prev.href = pageHref(page - 1);
      prev.className = 'pagination__item pagination__item--next pagination__item-arrow link motion-reduce';
      prev.setAttribute('aria-label', 'Página anterior');
      prev.textContent = '‹';
      prevItem.appendChild(prev);
      list.appendChild(prevItem);

      const previousNumberItem = document.createElement('li');
      const previousNumber = document.createElement('a');
      previousNumber.href = pageHref(page - 1);
      previousNumber.className = 'pagination__item link';
      previousNumber.setAttribute('aria-label', `Página ${page - 1}`);
      previousNumber.textContent = String(page - 1);
      previousNumberItem.appendChild(previousNumber);
      list.appendChild(previousNumberItem);
    }

    const currentItem = document.createElement('li');
    const current = document.createElement('span');
    current.className = 'pagination__item pagination__item--current light';
    current.setAttribute('aria-current', 'page');
    current.textContent = String(page);
    currentItem.appendChild(current);
    list.appendChild(currentItem);

    if (hasNext) {
      const nextNumberItem = document.createElement('li');
      const nextNumber = document.createElement('a');
      nextNumber.href = pageHref(page + 1);
      nextNumber.className = 'pagination__item link';
      nextNumber.setAttribute('aria-label', `Página ${page + 1}`);
      nextNumber.textContent = String(page + 1);
      nextNumberItem.appendChild(nextNumber);
      list.appendChild(nextNumberItem);

      const nextItem = document.createElement('li');
      const next = document.createElement('a');
      next.href = pageHref(page + 1);
      next.className = 'pagination__item pagination__item--prev pagination__item-arrow link motion-reduce';
      next.setAttribute('aria-label', 'Próxima página');
      next.textContent = '›';
      nextItem.appendChild(next);
      list.appendChild(nextItem);
    }

    nav.appendChild(list);
    pager.appendChild(nav);
  };

  const ensureStyles = () => {
    if (document.getElementById('EntrevoltasUniqueStampCatalogStyles')) return;

    const style = document.createElement('style');
    style.id = 'EntrevoltasUniqueStampCatalogStyles';
    style.textContent = `
      .ev-stamp-pagination .pagination__item {
        min-width: 44px;
        min-height: 44px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
    `;
    document.head.appendChild(style);
  };

  const build = async (container) => {
    const grid = container.querySelector(GRID_SELECTOR);
    if (!grid) return;

    const sectionId = grid.dataset.id;
    if (!sectionId) return;

    const page = currentUniquePage();
    const pageSize =
      Number.parseInt(grid.dataset.entrevoltasPageSize || '24', 10) || 24;
    const signature = `${querySignature(sectionId)}|unique-page:${page}|size:${pageSize}`;

    if (grid.dataset.entrevoltasUniqueSignature === signature) return;

    let state = states.get(container);
    if (!state) {
      state = { sequence: 0, controller: null, observer: null, timer: null };
      states.set(container, state);
    }

    state.sequence += 1;
    const sequence = state.sequence;

    state.controller?.abort();
    state.controller = new AbortController();

    container.classList.add('ev-unique-catalog-loading');

    try {
      const result = await collectUniqueCards({
        grid,
        sectionId,
        requestedPage: page,
        pageSize,
        signal: state.controller.signal,
      });

      if (sequence !== state.sequence) return;

      const start = (page - 1) * pageSize;
      const end = start + pageSize;
      const pageCards = result.cards.slice(start, end);
      const hasNext = result.cards.length > end;

      if (!pageCards.length && page > 1) {
        window.location.replace(pageHref(1));
        return;
      }

      const fragment = document.createDocumentFragment();

      pageCards.forEach((card, index) => {
        if (card.hasAttribute('data-cascade')) {
          card.style.setProperty('--animation-order', String(index + 1));
        }
        fragment.appendChild(card);
      });

      grid.replaceChildren(fragment);
      grid.dataset.entrevoltasUniqueSignature = signature;
      grid.dataset.entrevoltasUniqueCount = String(pageCards.length);
      grid.dataset.entrevoltasUniquePage = String(page);

      renderPager({ grid, page, hasNext });
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[Entrevoltas] Não foi possível montar a vitrine única por estampa.', error);
      }
    } finally {
      if (sequence === state.sequence) {
        container.classList.remove('ev-unique-catalog-loading');
      }
    }
  };

  const scheduleBuild = (container) => {
    let state = states.get(container);

    if (!state) {
      state = { sequence: 0, controller: null, observer: null, timer: null };
      states.set(container, state);
    }

    if (state.timer) window.clearTimeout(state.timer);

    state.timer = window.setTimeout(() => {
      state.timer = null;
      build(container);
    }, 60);
  };

  const observe = (container) => {
    let state = states.get(container);

    if (!state) {
      state = { sequence: 0, controller: null, observer: null, timer: null };
      states.set(container, state);
    }

    if (state.observer) return;

    state.observer = new MutationObserver(() => {
      const grid = container.querySelector(GRID_SELECTOR);
      if (!grid) return;

      const sectionId = grid.dataset.id;
      const page = currentUniquePage();
      const pageSize =
        Number.parseInt(grid.dataset.entrevoltasPageSize || '24', 10) || 24;
      const signature = `${querySignature(sectionId)}|unique-page:${page}|size:${pageSize}`;

      if (grid.dataset.entrevoltasUniqueSignature === signature) return;
      scheduleBuild(container);
    });

    state.observer.observe(container, { childList: true });
  };

  const init = () => {
    ensureStyles();

    document.querySelectorAll(CONTAINER_SELECTOR).forEach((container) => {
      if (!container.querySelector(GRID_SELECTOR)) return;
      observe(container);
      scheduleBuild(container);
    });
  };

  if (window[GLOBAL_FLAG]) {
    init();
    return;
  }

  window[GLOBAL_FLAG] = true;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  window.addEventListener('popstate', () => window.setTimeout(init, 0));
})();
